import { css } from '@emotion/react';
import {
  type ComponentProps, useCallback,
  useEffect, useRef,
  useState,
} from 'react';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import { RadioGroupComponent } from '~/_shared/baseComponents/radio/radioGroup.component';
import { TooltipComponent } from '~/_shared/baseComponents/tooltip/tooltip.component';
import { createColor } from '~/_shared/components/colorPicker/colorPicker.helpers';
import { ColorPickerWithInputBarComponent } from '~/_shared/components/colorPicker/colorPickerWithInputBar/colorPickerWithInputBar.component';
import { useTranslation } from '~/_shared/utils/hooks';
import { usePrevious } from '~/_shared/utils/hooks/usePrevious';
import type { BoundaryGroup } from '~/boundary/boundary.types';
import { CustomLabelConfigurationComponent } from '~/boundary/settings/boundaryLabels/customLabelConfiguration.component';
import { type ColorPickerComponent } from '~/boundary/settings/colorPicker/colorPicker.component';
import { checkboxStyle } from '~/boundary/settings/common/commonSettingsComponent';
import { SIDES_PADDING } from '~/boundary/settings/constants';
import type {
  BoundaryTerritoryGroup, BoundaryTerritoryLabelValueConfig,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';

type LabelValueType = 'fill' | 'custom';

type BoundaryLabelSettingsComponentProps = {
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
  disallowCustomValue?: boolean;
  boundaryGroup: BoundaryGroup;
  onBoundaryTerritoryGroupChange: (territoryGroup: BoundaryTerritoryGroup) => void;
  setIsFormValid: (isFormValid: boolean) => void;
  setIsLoading: (isFormValid: boolean) => void;
};

const containerStyle = css({
  alignItems: 'flex-start',
  display: 'flex',
  gap: 20,
  paddingInline: SIDES_PADDING,
});

const customValueConfigContainerStyle = css({
  paddingInline: SIDES_PADDING,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const sectionStyle = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  gap: 8,
});

const headingStyle = css({
  textTransform: 'uppercase',
  fontSize: 16,
  fontWeight: 500,
  margin: 0,
});

const checkboxContainerStyle = css({
  display: 'flex',
  alignItems: 'center',
  padding: '4px 0',
  width: '100%',
});

const nestedStyle = css({
  paddingLeft: SIDES_PADDING,
});

const radioGroupStyle = css({
  width: '100%',
  gap: 8,
});

const radioLabelStyle = css({
  textTransform: 'uppercase',
  fontSize: 12,
  paddingLeft: 8,
});

const radioIndicatorStyle = (isChecked: boolean) => css({
  height: 16,
  width: 16,
  borderWidth: isChecked ? 4 : 2,
});

export const BoundaryLabelSettingsComponent = ({
  boundaryTerritoryGroup,
  disallowCustomValue,
  onBoundaryTerritoryGroupChange,
  setIsFormValid,
  setIsLoading,
  boundaryGroup,
}: BoundaryLabelSettingsComponentProps) => {
  const [t] = useTranslation();
  const valueCustomizationWrapperRef = useRef<HTMLDivElement>(null);
  const { settings } = boundaryTerritoryGroup;
  const labelIncludesName = !settings.hideLabels;
  const currentLabelValueSettings: BoundaryTerritoryLabelValueConfig = settings.labelValueConfig;
  const labelIncludesLegend = currentLabelValueSettings.includeLegend;
  const labelIncludesValue = currentLabelValueSettings.showValue;
  const [isCustomLabelFromValid, setCustomLabelFormValid] = useState(true);

  useEffect(() => {
    if (currentLabelValueSettings.isValueCustom) {
      setIsFormValid(isCustomLabelFromValid);
    }
    else {
      setIsFormValid(true);
    }
  }, [currentLabelValueSettings.isValueCustom, isCustomLabelFromValid, setIsFormValid]);

  const previousIsValueCustom = usePrevious(currentLabelValueSettings.isValueCustom);

  useEffect(() => {
    if (previousIsValueCustom === false && currentLabelValueSettings.isValueCustom) {
      valueCustomizationWrapperRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [currentLabelValueSettings.isValueCustom, previousIsValueCustom]);

  const handleLabelIncludesNameChange: ComponentProps<typeof CheckboxComponent>['checkedSetter'] = useCallback((event) => {
    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        hideLabels: !event.target.checked,
      },
    });
  }, [boundaryTerritoryGroup, onBoundaryTerritoryGroupChange]);

  const handleLabelShowValueChange: ComponentProps<typeof CheckboxComponent>['checkedSetter'] = useCallback((event) => {
    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        labelValueConfig: {
          ...currentLabelValueSettings,
          showValue: event.target.checked,
        },
      },
    });
  }, [boundaryTerritoryGroup, currentLabelValueSettings, onBoundaryTerritoryGroupChange]);

  const handleLabelIncludeLegendChange: ComponentProps<typeof CheckboxComponent>['checkedSetter'] = useCallback((event) => {
    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        labelValueConfig: {
          ...currentLabelValueSettings,
          includeLegend: event.target.checked,
        },
      },
    });
  }, [boundaryTerritoryGroup, currentLabelValueSettings, onBoundaryTerritoryGroupChange]);

  const handleLabelIsValueCustomChange: ComponentProps<typeof RadioGroupComponent<LabelValueType>>['onValueChange'] = useCallback((newValue) => {
    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        labelValueConfig: {
          ...currentLabelValueSettings,
          isValueCustom: newValue === 'custom',
        },
      },
    });
  }, [boundaryTerritoryGroup, currentLabelValueSettings, onBoundaryTerritoryGroupChange]);

  const handleLabelBackgroundColorChange: ComponentProps<typeof ColorPickerComponent>['onChange'] = useCallback((colorResult) => {
    const currentLabelStyle = boundaryTerritoryGroup.settings.labelStyle;

    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        labelStyle: {
          ...currentLabelStyle,
          backgroundColor: colorResult.hex,
        },
      },
    });
  }, [boundaryTerritoryGroup, onBoundaryTerritoryGroupChange]);

  const handleLabelTextColorChange: ComponentProps<typeof ColorPickerComponent>['onChange'] = useCallback((colorResult) => {
    const currentLabelStyle = boundaryTerritoryGroup.settings.labelStyle;

    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        labelStyle: {
          ...currentLabelStyle,
          fontColor: colorResult.hex,
        },
      },
    });
  }, [boundaryTerritoryGroup, onBoundaryTerritoryGroupChange]);

  const {
    backgroundColor: labelBackgroundColor,
    fontColor: labelTextColor,
  } = settings.labelStyle;

  return (
    <>
      <div css={containerStyle}>
        <div css={sectionStyle}>
          <h3 css={headingStyle}>
            {t('boundaryLabelConfig.customization')}
          </h3>
          <div>
            <div css={checkboxContainerStyle}>
              <TooltipComponent
                tooltipContent={boundaryGroup.labelsEnabled ? null :
                  t('boundaryLabelConfig.includeName.disabledTooltip', { boundaryGroupName: boundaryGroup.name })}
              >
                <CheckboxComponent
                  isChecked={labelIncludesName}
                  checkedSetter={handleLabelIncludesNameChange}
                  text={t('boundaryLabelConfig.includeName')}
                  textStyle={checkboxStyle}
                  isDisabled={!boundaryGroup.labelsEnabled && !labelIncludesName}
                />
              </TooltipComponent>
            </div>
            <div css={checkboxContainerStyle}>
              <CheckboxComponent
                isChecked={labelIncludesValue}
                checkedSetter={handleLabelShowValueChange}
                text={t('boundaryLabelConfig.includeValue')}
                textStyle={checkboxStyle}
              />
            </div>
            <div css={[checkboxContainerStyle, nestedStyle]}>
              <CheckboxComponent
                isDisabled={!labelIncludesValue}
                isChecked={labelIncludesLegend}
                checkedSetter={handleLabelIncludeLegendChange}
                text={t('boundaryLabelConfig.includeLegend')}
                textStyle={checkboxStyle}
              />
            </div>
            {!disallowCustomValue && (
              <div css={[checkboxContainerStyle, nestedStyle]}>
                <RadioGroupComponent<LabelValueType>
                  radioIconStyle={radioIndicatorStyle}
                  css={radioGroupStyle}
                  items={[{
                    value: 'fill',
                    label: <span css={radioLabelStyle}>{t('boundaryLabelConfig.fillValue')}</span>,
                    isDisabled: !labelIncludesValue,
                  }, {
                    value: 'custom',
                    label: <span css={radioLabelStyle}>{t('boundaryLabelConfig.customValue')}</span>,
                    isDisabled: !labelIncludesValue,
                  }]}
                  selectedValue={currentLabelValueSettings.isValueCustom ? 'custom' : 'fill'}
                  onValueChange={handleLabelIsValueCustomChange}
                />
              </div>
            )}
          </div>
        </div>
        <div css={sectionStyle}>
          <h3 css={headingStyle}>
            {t('BackgroundColor')}
          </h3>
          <ColorPickerWithInputBarComponent
            isFixed
            selectedColor={createColor(labelBackgroundColor)}
            onChange={handleLabelBackgroundColorChange}
            displayAlpha={false}
          />
          <h3 css={headingStyle}>
            {t('FontColor')}
          </h3>
          <ColorPickerWithInputBarComponent
            isFixed
            selectedColor={createColor(labelTextColor)}
            onChange={handleLabelTextColorChange}
            displayAlpha={false}
          />
        </div>
      </div>
      {!disallowCustomValue && currentLabelValueSettings.isValueCustom && (
        <div
          css={customValueConfigContainerStyle}
          ref={valueCustomizationWrapperRef}
        >
          <CustomLabelConfigurationComponent
            boundaryTerritoryGroup={boundaryTerritoryGroup}
            onBoundaryTerritoryGroupChange={onBoundaryTerritoryGroupChange}
            setIsFormValid={setCustomLabelFormValid}
            setIsLoading={setIsLoading}
          />
        </div>
      )}
    </>
  );
};
